/** @jsxImportSource @emotion/react */
import React from 'react'

import Button from 'components/atoms/Button'
import Toast from 'components/atoms/Toast'
import DashboardContent from 'components/lab/atoms/DashboardContent'
import routes from 'components/lab/constants/routes'
import { UserPermission } from 'components/lab/user/permissions'
import { confirm } from 'components/molecules/Confirmation'
import { PLATFORM_MODE_HINTS, PLATFORM_MODE_LABELS, PlatformMode } from 'constants/platformMode'
import { useLabPermission } from 'hooks/useLabPermission'
import {
  useGetPlatformSettingsQuery,
  useUpdatePlatformSettingsMutation,
} from 'store/services/platformSettings'

// Matches the section cards on the lab user form.
const CARD_HEADER_CSS = {
  color: 'black !important',
  backgroundColor: 'transparent !important',
}

// System-wide TestSmarter / iRhythm mode. Any lab user can see it; the permission lets it be changed.
export const PlatformSettings: React.FC = () => {
  const canManage = useLabPermission(UserPermission.MANAGE_PLATFORM_SETTINGS)
  const { data, isLoading } = useGetPlatformSettingsQuery()
  const [updateSettings, { isLoading: isSaving }] = useUpdatePlatformSettingsMutation()
  const [mode, setMode] = React.useState<PlatformMode>()

  React.useEffect(() => {
    if (data) setMode(data.platformMode)
  }, [data])

  const canEdit = canManage && (data?.canEdit ?? false)

  const handleSave = async () => {
    if (!mode) return

    try {
      await confirm('', {
        title: `Switch to ${PLATFORM_MODE_LABELS[mode]} mode?`,
        description: () => (
          <div className="alert alert-warning mb-0">
            <i className="fas fa-exclamation-triangle mr-2" />
            This changes what every DME user, email and integration receives.{' '}
            {PLATFORM_MODE_HINTS[mode]}
          </div>
        ),
        yesAction: `Switch to ${PLATFORM_MODE_LABELS[mode]}`,
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    try {
      await updateSettings({ platformMode: mode }).unwrap()
      Toast({ type: 'success', label: `${PLATFORM_MODE_LABELS[mode]} mode is on` })
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Unable to save platform settings'
      Toast({ type: 'error', label: message })
    }
  }

  return (
    <DashboardContent
      title="PLATFORM SETTINGS"
      icon="fas fa-sliders-h"
      content={
        isLoading || !data || !mode ? (
          <div className="card card-primary">
            <div className="card-body">Loading...</div>
          </div>
        ) : (
          <>
            {!canEdit && (
              <div className="alert alert-info">
                You can see this setting but not change it. Ask an administrator for the
                &ldquo;Manage platform settings&rdquo; permission.
              </div>
            )}

            <div className="card card-primary" css={{ maxWidth: 640 }}>
              <div className="card-header" css={CARD_HEADER_CSS}>
                <h3 className="card-title">PLATFORM MODE</h3>
              </div>
              <div className="card-body">
                {Object.values(PlatformMode).map((value) => (
                  <div key={value} className="form-check" css={{ marginBottom: '1rem' }}>
                    <input
                      id={`platform-mode-${value}`}
                      className="form-check-input"
                      type="radio"
                      name="platformMode"
                      disabled={!canEdit}
                      checked={mode === value}
                      onChange={() => setMode(value)}
                    />
                    <label className="form-check-label" htmlFor={`platform-mode-${value}`}>
                      <b>{PLATFORM_MODE_LABELS[value]}</b>
                      <small className="form-text text-muted">{PLATFORM_MODE_HINTS[value]}</small>
                    </label>
                  </div>
                ))}
                <small className="form-text text-muted">
                  Applies to every user, email and integration. Open DME sessions pick it up on
                  their next page load.
                </small>
              </div>
            </div>

            {canEdit && (
              <Button
                className="btn btn-primary"
                label={isSaving ? 'Saving…' : 'Save'}
                disabled={isSaving || mode === data.platformMode}
                onClick={handleSave}
              />
            )}
          </>
        )
      }
      breadcrumb={[
        {
          label: 'Platform Settings',
          to: `${routes.index}${routes.platformSettings.root}`,
        },
      ]}
    />
  )
}

export default PlatformSettings
